/**
 * Cross-Reference Heatmap data (doc 147 §6.6 E5 + PR Q1 stratify/sort/brush).
 *
 * `buildHeatmapMatrix` is the original undirected N×N reference-count matrix
 * kept for backwards-compat. PR Q1 adds:
 *  - `buildStratifiedHeatmap(manifest, { refKind })` — same matrix but filtered
 *    to a single reference kind (FK / embed / enum). Stratification reveals
 *    structurally-different graphs hidden inside the combined view.
 *  - `sortHeatmap(matrix, { by, dir })` — reorder rows + cols by row total or
 *    by entity name. Pure; returns a new matrix.
 *  - `brushSelection(matrix, manifest, rect)` — extract the entities + ref
 *    count inside a brushed rectangle (used by the zoom-to-quadrant CTA).
 *  - `cellReferences(matrix, manifest, i, j)` — the underlying references for
 *    a single cell (used by the drill-down panel).
 */
import type { AtlasManifest, Reference, ReferenceKind } from '@atlas-manifest/types';

export interface HeatmapMatrix {
  entityIds: string[];
  cells: number[][];
  maxCell: number;
}

export interface StratifyOptions {
  refKind?: ReferenceKind;
}

/** Legacy entry point — builds with no kind filter. */
export function buildHeatmapMatrix(manifest: AtlasManifest): HeatmapMatrix {
  return buildStratifiedHeatmap(manifest);
}

export function buildStratifiedHeatmap(
  manifest: AtlasManifest,
  opts: StratifyOptions = {},
): HeatmapMatrix {
  const entityIds = manifest.entities.map((e) => e.id).sort();
  const n = entityIds.length;
  const idx = new Map(entityIds.map((id, i) => [id, i]));
  const cells: number[][] = Array.from({ length: n }, () => Array(n).fill(0) as number[]);
  let maxCell = 0;

  for (const ref of manifest.references) {
    if (ref.from === ref.to) continue;
    if (opts.refKind && ref.kind !== opts.refKind) continue;
    const i = idx.get(ref.from);
    const j = idx.get(ref.to);
    if (i === undefined || j === undefined) continue;
    cells[i]![j]! += 1;
    cells[j]![i]! += 1;
    if (cells[i]![j]! > maxCell) maxCell = cells[i]![j]!;
  }
  return { entityIds, cells, maxCell };
}

export interface SortHeatmapOptions {
  by: 'rowTotal' | 'name';
  dir: 'asc' | 'desc';
}

/** Return a new matrix with rows + cols reordered. */
export function sortHeatmap(
  matrix: HeatmapMatrix,
  opts: SortHeatmapOptions,
): HeatmapMatrix {
  const n = matrix.entityIds.length;
  const totals = matrix.cells.map((row) => row.reduce((acc, v) => acc + v, 0));
  const sign = opts.dir === 'asc' ? 1 : -1;

  const order = matrix.entityIds.map((id, i) => ({ id, i, total: totals[i]! }));
  order.sort((a, b) => {
    if (opts.by === 'rowTotal') {
      if (a.total !== b.total) return (a.total - b.total) * sign;
      return a.id.localeCompare(b.id);
    }
    return a.id.localeCompare(b.id) * sign;
  });

  const reorderedIds = order.map((o) => o.id);
  const reorderedCells: number[][] = Array.from({ length: n }, () => Array(n).fill(0) as number[]);
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      reorderedCells[r]![c] = matrix.cells[order[r]!.i]![order[c]!.i]!;
    }
  }
  return { entityIds: reorderedIds, cells: reorderedCells, maxCell: matrix.maxCell };
}

export interface BrushRect {
  rowStart: number;
  rowEnd: number;
  colStart: number;
  colEnd: number;
}

export interface BrushResult {
  entities: Set<string>;
  refCount: number;
}

export function brushSelection(
  matrix: HeatmapMatrix,
  manifest: AtlasManifest,
  rect: BrushRect,
): BrushResult {
  const n = matrix.entityIds.length;
  const rs = Math.max(0, rect.rowStart);
  const re = Math.min(n - 1, rect.rowEnd);
  const cs = Math.max(0, rect.colStart);
  const ce = Math.min(n - 1, rect.colEnd);

  const entities = new Set<string>();
  for (let r = rs; r <= re; r++) entities.add(matrix.entityIds[r]!);
  for (let c = cs; c <= ce; c++) entities.add(matrix.entityIds[c]!);

  // Count references where BOTH endpoints fall within the brushed entity set.
  let refCount = 0;
  for (const ref of manifest.references) {
    if (ref.from === ref.to) continue;
    if (entities.has(ref.from) && entities.has(ref.to)) refCount++;
  }
  return { entities, refCount };
}

export function cellReferences(
  matrix: HeatmapMatrix,
  manifest: AtlasManifest,
  i: number,
  j: number,
): Reference[] {
  const a = matrix.entityIds[i];
  const b = matrix.entityIds[j];
  if (!a || !b) return [];
  return manifest.references.filter(
    (r) =>
      (r.from === a && r.to === b) ||
      (r.from === b && r.to === a),
  );
}
